import { ButtonWorkspaceDropdown } from "@nova/component-btn"
import { DateFormat, TimeFormat } from "@nova/format"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { formItem } from "@nova/component-organization-settings"
import { AreaUnit, DistanceUnit, SupportedRegions } from "@nova/constants"
import { findEnumKeyByValue } from "@nova/util-strings"
import { selectActiveWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { noop } from "nodash"
import { setGlobalNotificationContext } from "user/profile/slice"

/**
 * To add a new field -
 *
 * name: how to refer to it internally. Must be unique
 * title: how to refer to it in the interface
 * type: Radio or Text or Checkbox
 * options: a parameter for the radio type, the radios that are present in the group. Label/Value for UI/Internal
 * label: a parameter for the checkbox type, the text beside the checkbox
 * defaultValue: the default value for field. Please use string for Radios/Text and boolean for Checkboxes
 *
 */
export const organizationSettingsFields: formItem[] = [
    {
        name: "region",
        title: "Data Residency",
        type: "Arbitrary",
        options: [
            { label: "USA", value: SupportedRegions.USA },
            { label: "Canada", value: SupportedRegions.Canada },
            { label: "Australia", value: SupportedRegions.Australia },
        ],
        defaultValue: SupportedRegions.USA,
        Component: () => {
            const dispatch = useAppDispatch()
            const activeWorkspace = useAppSelector(selectActiveWorkspace())
            const region =
                (activeWorkspace?.settings?.find((setting) => setting.name === "region")
                    ?.value as string) || SupportedRegions.Canada
            //need this custom component so we can get the global notification to show
            return (
                <div
                    onClick={() => {
                        dispatch(
                            setGlobalNotificationContext({
                                message: "Contact us to change regions",
                                status: GlobalNotificationStatus.warning,
                                timeoutInMs: 5000,
                            })
                        )
                    }}
                >
                    <ButtonWorkspaceDropdown
                        widthClass="w-full"
                        label={
                            findEnumKeyByValue(SupportedRegions, region) || SupportedRegions.Canada
                        }
                        dropdownButtons={[]}
                        setMenuOpen={noop}
                        menuOpen={false}
                        disabled={true}
                    />
                </div>
            )
        },
    },
    {
        name: "distance",
        title: "Distance",
        type: "Dropdown",
        options: [
            { label: "m/km", value: DistanceUnit.Meters },
            { label: "ft/mi", value: DistanceUnit.Feet },
        ],
        defaultValue: DistanceUnit.Feet,
    },
    {
        name: "area",
        title: "Area",
        type: "Dropdown",
        options: [
            { label: "ft\xB2/mi\xB2", value: AreaUnit.SquareMiles },
            { label: "ha", value: AreaUnit.Hectares },
            { label: "m\xB2/km\xB2", value: AreaUnit.SquareKilometers },
            { label: "ac", value: AreaUnit.Acres },
        ],
        defaultValue: AreaUnit.Acres,
    },

    {
        name: "time",
        title: "Time",
        type: "Dropdown",
        options: [
            { label: "12 Hours", value: TimeFormat.TwelveHour },
            { label: "24 Hours", value: TimeFormat.TwentyFourHour },
        ],
        defaultValue: TimeFormat.TwentyFourHour,
    },
    {
        name: "date",
        title: "Date Format",
        type: "Dropdown",
        options: [
            { label: "MM/DD/YYYY", value: DateFormat.MonthDayYear },
            { label: "DD/MM/YYYY", value: DateFormat.DayMonthYear },
            { label: "YYYY/DD/MM", value: DateFormat.YearDayMonth },
            { label: "YYYY/MM/DD", value: DateFormat.YearMonthDay },
        ],
        defaultValue: DateFormat.YearMonthDay,
    },
]
